import { Big } from "big.js";

import { Network } from "@/interfaces/network";
import { AirdropNFTProps, AirdropNFTStatus, NFTProps } from "@/interfaces/nft";
import { AirdropProps, AirdropStatus } from "@/interfaces/store";
import { AirdropResponse, OnchainNFT } from "@/services/rpc/near";

import { convertToDays } from "./date";

const preActiveTime = 10 * 60 * 1000;

const getStatus = ({
  isStopped,
  startDate,
  total,
  claimed,
}: {
  isStopped: boolean;
  startDate: number;
  total: string;
  claimed: string;
}): AirdropStatus => {
  if (isStopped) return "stopped";
  const now = new Date().getTime();
  const startInMilliseconds = startDate / 1000000;

  if (startInMilliseconds - now <= preActiveTime && startInMilliseconds > now) {
    return "preActive";
  }

  if (startInMilliseconds > now) return "soon";
  console.log(claimed, total, Big(claimed).lt(total));
  if (Big(claimed).lt(total)) return "active";
  return "closed";
};

const determineNftStatus = (
  airdrop: AirdropResponse,
  nft: { nftId: string; status?: AirdropNFTStatus },
  holdTimeInDays: number | undefined,
  ownershipInDays: number,
  balance: string | null,
  index: number,
  userClaimsCount = "0",
): AirdropNFTStatus => {
  if (airdrop.available_nft_ids && !airdrop.available_nft_ids.includes(nft.nftId)) {
    return "claimed";
  }
  const hapiLimit = airdrop.one_reward_per_claim
    ? (index + 1) * Number(airdrop.required_token_balance)
    : airdrop.required_token_balance;

  if (
    (!holdTimeInDays || ownershipInDays >= holdTimeInDays) &&
    Big(balance ?? "0")
      .minus(userClaimsCount)
      .gte(hapiLimit)
  ) {
    return "fulfilled";
  }
  return "rejected";
};

export const formatAirdrop = (
  airdrop: AirdropResponse,
  nfts: NFTProps[] | AirdropNFTProps[] | null | undefined,
  minAllowedScore: number,
  hapiBalance: string | null,
): AirdropProps => {
  const status = getStatus({
    isStopped: airdrop.is_stopped,
    startDate: airdrop.start_date,
    total: airdrop.total_amount,
    claimed: airdrop.claimed_amount,
  });
  const isHAPIBalanceRequired = airdrop.required_token_balance ? Number(airdrop.required_token_balance) > 0 : false;

  const holdTimeInDays = convertToDays(airdrop.required_hold_time, "nanoseconds");
  const formattedNFTs: Array<AirdropNFTProps> | undefined = nfts?.map((nft, index) => {
    let ownershipInDays: number;
    if (nft.updated_at) {
      const updatedAtInMilliseconds = nft.updated_at / 1_000_000;
      const heldDuration = Date.now() - updatedAtInMilliseconds;
      ownershipInDays = convertToDays(heldDuration, "milliseconds");
    } else {
      ownershipInDays = holdTimeInDays;
    }

    const nftStatus: AirdropNFTStatus =
      "status" in nft
        ? airdrop.available_nft_ids
          ? determineNftStatus(
              airdrop,
              nft,
              holdTimeInDays,
              ownershipInDays,
              hapiBalance,
              index,
              airdrop.userClaimsCount,
            )
          : nft.status
        : determineNftStatus(
            airdrop,
            nft,
            holdTimeInDays,
            ownershipInDays,
            hapiBalance,
            index,
            airdrop.userClaimsCount,
          );

    return { ...nft, ownershipInDays: ownershipInDays, status: nftStatus };
  });
  console.log(status);
  return { ...airdrop, minAllowedScore, isHAPIBalanceRequired, holdTimeInDays, status, nfts: formattedNFTs || null };
};

export const formatOnchainNft = (nfts?: OnchainNFT[]): NFTProps[] => {
  return (
    nfts?.map((el) => ({
      network: Network.NEAR,
      url: el.metadata.media,
      nftId: el.token_id,
      title: el.metadata.title,
      description: el.metadata.description,
      updated_at: el.metadata.updated_at,
    })) ?? []
  );
};
